import type { Upload } from '@tabservice/tabfile-client';
import { Button } from '@/components/ui/button';
import { Table, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { UploadStatusPill } from './status-pill';
import { formatMeta } from '@/features/new-filing/formats';
import { formatCount, formatDate } from '@/lib/format';

/** True while Tab Service is still reading and checking the draft's file. */
export function isDraftBeingChecked(upload: Upload): boolean {
  return upload.status === 'received' || upload.status === 'parsing';
}

/** What a draft is waiting for, in the customer's words. */
export function draftStage(upload: Upload): string {
  if (upload.status === 'awaiting_file') return upload.fileName ? 'File not fully uploaded' : 'No file chosen yet';
  if (isDraftBeingChecked(upload)) return 'Being checked';
  if (upload.status === 'checked') return `${formatCount(upload.recordCount ?? 0)} records ready to submit`;
  if (upload.status === 'blocked') return 'Needs attention before it can be submitted';
  if (upload.status === 'failed') return 'Could not be read';
  return upload.status;
}

/**
 * E-files that were started and not submitted. Each row is an upload Tab Service holds:
 * open it to carry on from the step it is waiting at, or discard it.
 */
export function DraftsTable({
  items,
  busy,
  onOpen,
  onDiscard,
  discarding,
}: {
  items: Upload[];
  busy?: boolean;
  onOpen: (upload: Upload) => void;
  onDiscard: (uploadId: string) => void;
  discarding: string | null;
}) {
  return (
    <TableWrap busy={busy}>
      <Table>
        <THead>
          <tr>
            <TH>File</TH>
            <TH>Format</TH>
            <TH>Form</TH>
            <TH>Tax year</TH>
            <TH>Status</TH>
            <TH>Started</TH>
            <TH className="w-48" aria-label="Actions" />
          </tr>
        </THead>
        <TBody>
          {items.map((upload) => (
            <TR
              key={upload.id}
              interactive
              tabIndex={0}
              role="link"
              aria-label={`Resume ${upload.fileName || 'draft'}`}
              onClick={() => onOpen(upload)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onOpen(upload);
                }
              }}
            >
              <TD className="font-medium">
                {upload.fileName || 'No file yet'}
                {upload.amendsEfileId ? (
                  <span className="block text-sm font-normal text-muted-foreground">
                    {upload.amendKind === 'correction' ? 'Correction' : 'Replacement'} for {upload.amendsEfileId}
                  </span>
                ) : null}
              </TD>
              <TD className="text-muted-foreground">{formatMeta[upload.format]?.label ?? upload.format}</TD>
              <TD className="text-muted-foreground">{upload.formType ?? 'From the file'}</TD>
              <TD className="text-muted-foreground">{upload.taxYear ?? '-'}</TD>
              <TD>
                <div className="flex flex-wrap items-center gap-2">
                  <UploadStatusPill status={upload.status} />
                  <span className="text-muted-foreground">{draftStage(upload)}</span>
                </div>
              </TD>
              <TD className="whitespace-nowrap text-muted-foreground">{formatDate(upload.createdAt)}</TD>
              <TD className="text-right">
                <div className="flex justify-end gap-2" onClick={(event) => event.stopPropagation()}>
                  <Button variant="outline" size="sm" onClick={() => onOpen(upload)}>
                    Resume
                  </Button>
                  <Button variant="ghost" size="sm" disabled={discarding === upload.id} onClick={() => onDiscard(upload.id)}>
                    Discard
                  </Button>
                </div>
              </TD>
            </TR>
          ))}
        </TBody>
      </Table>
    </TableWrap>
  );
}
